import { TPpdApp } from "../../../App/PpdApp";
import TrateSelectionMap from "../../../Map/TrateSelectionMap";
import { SpoBaseTask } from "../SpoBase/SpoBaseTask";

export class TTrateTask extends SpoBaseTask {

    /**
     * Inicializuje úlohu. Při příchodu z mapy přeskočí informační dialog
     * a po načtení dat rovnou otevře požadovaný detail tratě.
     */
    public initialize(): void {
        const trackNumber = this.getRequestedTrackNumber();
        if (!trackNumber)
            return;

        this.setRequestedTrackToSearchFilter(trackNumber);
        this.clearOpenDetailQueryParams();
        this.initOpenTrackDetailAwareness();
    }

    /**
     * Při příchodu z Mapy nastaví číslo tratě do filtru vyhledávání.
     */
    private setRequestedTrackToSearchFilter(trackNumber: string): void {
        this.userStoredRibbonFilterValues.Set("searchString", trackNumber);

        const txtSearch = $("#txtSearch").dxTextBox("instance");
        if (!txtSearch)
            return;

        const eventHandler = txtSearch.option("onValueChanged");
        txtSearch.option("onValueChanged", null);
        txtSearch.option("value", trackNumber);
        txtSearch.option("onValueChanged", eventHandler);
    }

    /**
     * Informuje uživatele, že datum bylo převzato z filtru úlohy Mapa.
     * Po potvrzení se načtou data k tomuto datu a otevře se detail tratě.
     */
    private initOpenTrackDetailAwareness(): void {
        const date = this.getInitialDateValue();
        const formattedDate =
            `${date.getDate().toString().padStart(2, "0")}.`
            + `${(date.getMonth() + 1).toString().padStart(2, "0")}.`
            + date.getFullYear();

        this.App.showMessageBoxEx(
            "Upozornění",
            `Datum je nastaveno podle výběru v úloze Mapa, tj. ${formattedDate}.`,
            "CurrentTask",
            "setTodayDateAfterAwareness",
            "Ok1"
        );
    }

    /**
     * Při příchodu z mapy převezme datum z mapového filtru.
     */
    protected override getInitialDateValue(): Date {
        const raw = (document.getElementById("hdnOpenTrackDate") as HTMLInputElement | null)?.value.trim() ?? "";
        const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
        if (!match)
            return super.getInitialDateValue();

        const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
        return Number.isNaN(date.getTime()) ? super.getInitialDateValue() : date;
    }

    /**
     * Po prvním načtení dat otevře detail tratě předané v URL.
     */
    protected override async afterInitialDataLoaded(): Promise<void> {
        const trackNumber = this.getRequestedTrackNumber();
        if (!trackNumber)
            return;

        const trackInput = document.getElementById("hdnOpenTrackDetail") as HTMLInputElement | null;
        const dateInput = document.getElementById("hdnOpenTrackDate") as HTMLInputElement | null;

        if (trackInput)
            trackInput.value = "";
        if (dateInput)
            dateInput.value = "";

        await this.showDetailPopup(trackNumber);
    }

    /**
     * Odstraní jednorázové parametry detailu z historie prohlížeče.
     */
    private clearOpenDetailQueryParams(): void {
        const url = new URL(window.location.href);
        url.searchParams.delete("track");
        url.searchParams.delete("date");

        window.history.replaceState(
            null,
            document.title,
            `${url.pathname}${url.search}${url.hash}`
        );
    }

    /** Vrátí číslo tratě předané z úlohy Mapa. */
    private getRequestedTrackNumber(): string | null {
        const value = (document.getElementById("hdnOpenTrackDetail") as HTMLInputElement | null)?.value.trim() ?? "";
        return value.length > 0 ? value : null;
    }

    /** Instance gridu detailu tratí. */
    TrateDetailGridInstance: DevExpress.ui.dxDataGrid;

    /**
     * Klik na řádek gridu — synchronizuje focus na mapě.
     */
    public TrateDetailGrid_RowClick(e: DevExpress.ui.dxDataGrid.RowClickEvent): void {
        (CurrentTask as TTrateTask).EmptyFocusAndClearSelection();

        const id = e.data["ID"];
        const map = globalThis.OGMap as TrateSelectionMap;
        const idString = "Point-" + id;

        if (idString == map.FocusedLayer)
            return;

        this.TrateDetailGridInstance = $("#trateDetailGrid").dxDataGrid("instance");
        if (!this.TrateDetailGridInstance)
            return;

        (CurrentTask as TTrateTask).TrateDetailGridInstance.selectRows([e.data["PORADI"]], true);

        // Pokud bod od není na mapě: ID = 0 (SR70 nenalezeno v DimTrafficPoints)
        if (!id || !map?.FMap || !map.GetPointWithId?.(id))
            return;

        map.FMap.SetFocus(id, "Point");
        map.FocusedLayer = idString;
    }

    /**
     * Ruší focus na mapě a označení v gridu.
     */
    public EmptyFocusAndClearSelection(): void {
        globalThis.OGMap?.FMap?.ClearFocus();
        // Vždy čerstvá instance
        this.TrateDetailGridInstance = $("#trateDetailGrid").dxDataGrid("instance");
        this.TrateDetailGridInstance?.clearSelection();
    }

    /**
     * Označí řádek v gridu odpovídající danému ID bodu.
     * Pokud je nutná změna stránky, počká na contentReady před selectRows —
     * zabraňuje souběžným Oracle requestům v GetDetailData (NRE v OracleFailoverMgrImpl).
     */
    public async SelectGridRow(id: number, changePage: boolean = true): Promise<void> {
        // Vždy čerstvá instance — nikoli ??= (lazy cache)
        this.TrateDetailGridInstance = $("#trateDetailGrid").dxDataGrid("instance");

        if (!this.TrateDetailGridInstance)
            return;

        const ds = this.TrateDetailGridInstance.getDataSource();
        if (!ds)
            return;

        const store = ds.store();
        if (!store)
            return;

        let rows: any[];
        try {
            const loadResult = await store.load({ filter: ["ID", "=", id] });
            rows = Array.isArray(loadResult) ? loadResult : (loadResult as any).data || [];
        } catch {
            return;
        }

        const row = rows?.[0];
        if (!row) return;

        if (changePage) {
            const targetPage = Math.trunc((row["PORADI"] - 1) / 10);
            const currentPage = this.TrateDetailGridInstance.pageIndex();

            if (currentPage !== targetPage) {
                // Počkat na contentReady po změně stránky.
                // Bez tohoto čekání jdou skip=N&take=10 a filter=[PORADI=X]
                // na server souběžně → race na Oracle connection → NRE.
                await new Promise<void>((resolve) => {
                    const grid = this.TrateDetailGridInstance;
                    const onContentReady = () => {
                        grid.off('contentReady', onContentReady);
                        resolve();
                    };
                    grid.on('contentReady', onContentReady);
                    grid.pageIndex(targetPage);
                });
            }
        }

        this.TrateDetailGridInstance.selectRows([row["PORADI"]], true);
    }

    /**
     * Nastaví stránku gridu podle pořadí záznamu.
     */
    public SetGridPage(poradi: number): void {
        this.TrateDetailGridInstance ??= $("#trateDetailGrid").dxDataGrid("instance");
        if (!this.TrateDetailGridInstance) return;
        this.TrateDetailGridInstance.pageIndex(Math.trunc((poradi - 1) / 10));
    }

    /**
     * Otevře odkaz na NVTV v novém okně.
     */
    public openWindowNvtv(): void {
        const url = (document.getElementById("hdnUrlTtpNvtv") as HTMLInputElement | null)?.value.trim();

        if (!url) {
            console.error("Chybí konfigurace 'UrlTTPNVTV'.");
            return;
        }

        window.open(url, "_blank", "noopener,noreferrer");
    }
}

ScriptInit(new TPpdApp(), new TTrateTask());
$(function () {
    (CurrentTask as TTrateTask).initialize();
});